import { useNavigate } from "react-router-dom";
import { OFFICE_TICKET_STATUS_LABELS } from "../../lib/ticket-status";
import { fmtCurrency } from "../../lib/display-labels";
import type { TicketPipelineStatus } from "../../types/dashboard-layout";

/**
 * The ticket pipeline, one bar per status, exactly as GET
 * /dashboard/ticket-pipeline sends it — in its order, with its counts and its
 * priced totals. Nothing is summed or regrouped here.
 *
 * It used to tally the ticket LIST into five buckets and sum `subtotal ??
 * total_amount` per bucket, which was a second answer to the question this
 * endpoint now answers once.
 *
 * ABSENT IS NOT ZERO. `priced_total` is null when no ticket in the status has a
 * subtotal, and that renders "—", never "$0". A status with SOME unpriced
 * tickets shows the priced total and says how many are not in it, because a
 * partial sum standing alone reads as the whole.
 *
 * Status hue: the five signal fills, grouped by meaning, with the label
 * telling apart the statuses that share one. An unknown status — the API
 * appends any it finds outside its vocabulary — gets slate and a "—" label,
 * never a guessed name.
 */
const STATUS_FILL: Record<string, string> = {
  draft: "bg-fi-signal-slate",
  field_approved: "bg-fi-signal-amber",   // waiting on the office
  office_approved: "bg-fi-signal-blue",   // waiting on the customer
  customer_approved: "bg-fi-signal-green", // done, billable
  invoiced: "bg-fi-signal-green",          // done, billed
  customer_rejected: "bg-fi-signal-red",
  rejected: "bg-fi-signal-red",
};

const labelOf = (status: string): string =>
  (OFFICE_TICKET_STATUS_LABELS as Record<string, string>)[status] ?? "—";

export function pipelineAmount(s: TicketPipelineStatus): { amount: string; note: string | null } {
  if (s.count === 0) return { amount: fmtCurrency(0, null, 0), note: null };
  const amount = fmtCurrency(s.priced_total, null, 0);
  const note = s.priced_total !== null && s.unpriced_count > 0 ? `+ ${s.unpriced_count} unpriced` : null;
  return { amount, note };
}

const TicketPipeline = ({ statuses }: { statuses: TicketPipelineStatus[] }) => {
  const navigate = useNavigate();
  const maxCount = Math.max(...statuses.map((s) => s.count), 1);

  return (
    <ul className="space-y-2.5" aria-label="Tickets by status">
      {statuses.map((s) => {
        const widthPct = s.count > 0 ? Math.max((s.count / maxCount) * 100, 12) : 0;
        const fill = STATUS_FILL[s.status] ?? "bg-fi-signal-slate";
        const { amount, note } = pipelineAmount(s);
        return (
          <li key={s.status} data-testid={`pipeline-${s.status}`}>
            <button
              type="button"
              onClick={() => navigate(`/tickets?status=${encodeURIComponent(s.status)}`)}
              className="flex w-full items-center gap-3 text-left transition-opacity hover:opacity-80"
            >
              <span className="w-28 shrink-0 text-right text-[11px] font-semibold text-fieldiq-muted sm:w-32">
                {labelOf(s.status)}
              </span>
              <span className="relative h-8 flex-1 overflow-hidden rounded-fi-control bg-fieldiq-dark/60">
                {s.count > 0 && (
                  <span
                    className={`absolute inset-y-0 left-0 rounded-fi-control ${fill}`}
                    // Never narrower than the count it carries, or its dark ink lands on the track.
                    style={{ width: `max(${widthPct}%, 2.5rem)` }}
                    aria-hidden="true"
                  />
                )}
                <span
                  className={`absolute inset-y-0 left-2 flex items-center text-sm font-extrabold tabular-nums ${
                    s.count > 0 ? "text-fi-signal-ink" : "text-fieldiq-muted"
                  }`}
                  data-testid="pipeline-count"
                >
                  {s.count}
                </span>
              </span>
              <span className="w-24 shrink-0 text-right">
                <span data-testid="pipeline-amount" className="block text-xs font-bold tabular-nums text-fieldiq-text">
                  {amount}
                </span>
                {note && <span className="block text-[10px] text-fieldiq-muted">{note}</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default TicketPipeline;
